// src/pages/AdminCouponManagement.jsx
import { useEffect, useMemo } from "react";
import { useThunk } from "../../hooks/useThunk";
import { useInput } from "../../hooks/use-input";
import showToast from "../../utils/toastNotifications";

import Input from "../../components/Form/Input";
import CustomButton from "../../components/Buttons/CustomButton";
import {
    fetchAdminCoupon,
    saveOrUpdateAdminCoupon,
} from "../../services/thunks/adminCouponThunks";
import { validateAdminCouponCode } from "../../utils/validationUtils";
import { validateDesc } from "../../utils/validationUtils";


export default function AdminCouponManagement() {
    // code
    const {
        value: code,
        handleInputChange: onCodeChange,
        handleInputBlur: onCodeBlur,
        hasError: codeHasError,
        setValue: setCode,
        errorMessage: codeErrorMessage,
        setErrorMessage: setCodeError,
        reset: resetCode,
    } = useInput("", (v) => validateAdminCouponCode(v));

    // english
    const {
        value: en,
        handleInputChange: onEnChange,
        handleInputBlur: onEnBlur,
        setValue: setEn,
        hasError: enHasError,
        errorMessage: enErrorMessage,
        setErrorMessage: setEnError,
        reset: resetEn,
    } = useInput("", (v) => validateDesc(v, "English text"));

    // german
    const {
        value: de,
        handleInputChange: onDeChange,
        handleInputBlur: onDeBlur,
        setValue: setGer,
        hasError: deHasError,
        errorMessage: deErrorMessage,
        setErrorMessage: setDeError,
        reset: resetDe,
    } = useInput("", (v) => validateDesc(v, "German text"));

    // Thunks
    const [doFetchCoupon, fetchingCoupon] = useThunk(fetchAdminCoupon);
    const [doSaveCoupon, savingCoupon] = useThunk(saveOrUpdateAdminCoupon);

    useEffect(() => {
        (async () => {
            const res = await doFetchCoupon();
            const data = res?.response?.data || res?.payload?.data || res?.payload;
            const doc = data?.data || data;
            console.log("doc", doc)
            if (!doc) return;

            setCode(doc?.code || "");
            setEn(doc?.description?.en || "");
            setGer(doc?.description?.de || "");
        })();
    }, []);

    const isBusy = useMemo(
        () => Boolean(fetchingCoupon || savingCoupon),
        [fetchingCoupon, savingCoupon]
    );

    const handleCancel = () => {
        // Reset back to last loaded values
        resetCode("");
        resetEn("");
        resetDe("");
        // Re-fetch to restore the persisted ones
        doFetchCoupon().then((res) => {
            const data = res?.response?.data || res?.payload?.data || res?.payload;
            const doc = data?.data || data;
            if (!doc) return;

            setCode(doc?.code || "");
            setEn(doc?.description?.en || "");
            setGer(doc?.description?.de || "");
        });
        showToast("info", "Changes discarded");
    };

    const handleSave = async () => {
        // run client-side checks first
        const codeErr = validateAdminCouponCode(code);
        const enErr = validateDesc(en, "English text");
        const deErr = validateDesc(de, "German text");

        setCodeError(codeErr);
        setEnError(enErr);
        setDeError(deErr);

        const firstErr = codeErr || enErr || deErr;
        if (firstErr) {
            showToast("error", firstErr);
            return;
        }

        const payload = {
            code: code.trim(),
            description: {
                en: en.trim(),
                de: de.trim(),
            },
        };

        const res = await doSaveCoupon(payload);
        const api = res?.response;
        const ok = res?.success ?? true;

        if (ok) {
            showToast("success", api?.message || "Coupon saved");
        } else {
            const msg =
                api?.message || api?.error || "Failed to save coupon. Please try again.";
            showToast("error", msg);
        }
    };

    return (
        <div className="px-6 py-10 text-white min-h-screen">
            <div className="mx-auto w-full max-w-5xl">
                <div className="rounded-2xl border border-[#1C2A36] bg-[#0a0f14] p-6 md:p-8">
                    {/* Coupon Code */}
                    <div className="mb-6">
                        <Input
                            id="couponCode"
                            name="couponCode"
                            label="Coupon Code"
                            placeholder="e.g., WELCOME10"
                            value={code}
                            onChange={onCodeChange}
                            onBlur={onCodeBlur}
                            isRequired
                            errorMessage={codeHasError ? codeErrorMessage : null}
                            className="bg-[#010101]"
                        />
                    </div>

                    {/* Coupon Code Text (English) */}
                    <div className="mb-6">
                        <Input
                            id="couponEn"
                            name="couponEn"
                            type="textarea"
                            rows={5}
                            label="Coupon Code Text (English)"
                            placeholder="Enter the English description to show users"
                            value={en}
                            onChange={onEnChange}
                            onBlur={onEnBlur}
                            isRequired
                            errorMessage={enHasError ? enErrorMessage : null}
                            className="bg-[#010101] min-h-[180px] py-4 px-4 text-base"
                        />
                    </div>

                    {/* Coupon Code Text (German) */}
                    <div className="mb-2">
                        <Input
                            id="couponDe"
                            name="couponDe"
                            type="textarea"
                            rows={5}
                            label="Coupon Code Text (German)"
                            placeholder="Geben Sie die deutsche Beschreibung für Benutzer ein"
                            value={de}
                            onChange={onDeChange}
                            onBlur={onDeBlur}
                            isRequired
                            errorMessage={deHasError ? deErrorMessage : null}
                            className="bg-[#010101] min-h-[180px] py-4 px-4 text-base"
                        />
                    </div>

                    <div className="mt-8 flex items-center justify-center gap-3">
                        <CustomButton
                            textColor="text-[#818181]"
                            borderColor="border-[#818181] border-[2px]"
                            textSize="text-[18px]"
                            font="font-jost"
                            padding="py-[6.5px] px-[36px]"
                            onClick={handleCancel}
                            disabled={isBusy}
                        >
                            Cancel
                        </CustomButton>

                        <CustomButton
                            useGradientBorder
                            textSize="text-[18px]"
                            font="font-jost"
                            padding="py-[6.5px] px-[36px]"
                            rounded="rounded-lg"
                            border="border-lg"
                            type="button"
                            onClick={handleSave}
                            disabled={isBusy}
                            loading={isBusy}
                        >
                            Save
                        </CustomButton>
                    </div>
                </div>
            </div>
        </div>
    );
}

